import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, X } from "lucide-react";
import { api, extractAxiosError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";

export function OrgAlertsBanner() {
    const { activeOrg } = useAuth();
    const [alerts, setAlerts] = useState([]);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        if (!activeOrg?.id || !can.removeMember(activeOrg?.role)) {
            setAlerts([]);
            return;
        }
        try {
            const { data } = await api.get(`/orgs/${activeOrg.id}/alerts`);
            setAlerts(data.alerts || []);
        } catch (_e) {
            setAlerts([]);
        }
    }, [activeOrg?.id, activeOrg?.role]);

    useEffect(() => { load(); }, [load]);

    const acknowledge = async (alert) => {
        if (!activeOrg?.id) return;
        setBusyId(alert.id);
        try {
            await api.post(`/orgs/${activeOrg.id}/alerts/${alert.id}/acknowledge`);
            setAlerts((prev) => prev.filter((a) => a.id !== alert.id));
        } catch (e) {
            toast.error(extractAxiosError(e, "Could not dismiss alert"));
        } finally {
            setBusyId(null);
        }
    };

    if (!alerts.length) return null;

    return (
        <div className="space-y-2 mb-6" data-testid="org-alerts-banner">
            {alerts.map((alert) => (
                <div
                    key={alert.id}
                    className="rounded-lg border border-amber-500/25 bg-amber-500/[0.06] px-4 py-3 flex gap-3 items-start"
                    data-testid={`org-alert-${alert.id}`}
                >
                    <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-amber-100/95">{alert.title}</p>
                        <p className="text-xs text-amber-100/70 mt-1 leading-relaxed">{alert.message}</p>
                        {alert.metadata?.allocation_after?.length > 0 && (
                            <p className="text-[11px] text-white/45 mt-2 font-mono">
                                New split:{" "}
                                {alert.metadata.allocation_after
                                    .map((m) => `${m.name} ${m.percentage}%`)
                                    .join(" · ")}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => acknowledge(alert)}
                        disabled={busyId === alert.id}
                        className="btn-ghost rounded-md p-1.5 text-white/45 hover:text-white shrink-0"
                        aria-label="Dismiss"
                        data-testid={`org-alert-dismiss-${alert.id}`}
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            ))}
        </div>
    );
}
